import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { MdBugReport, MdClose, MdContentCopy, MdDeleteSweep } from "react-icons/md";
import { useLocation } from "react-router-dom";
import { getCurrentElakayRole } from "../pageInside/Rolle";
import "./FamilyErrorDiagnostics.css";
import { elakayAutoText } from "../pageInside/dataElakay";


const MAX_ERRORS = 100;
const SECRET_PATTERN = /(authorization|bearer|token|password|secret|api[_-]?key)(\s*[:=]\s*|\s+)([^\s,;}]+)/gi;

const safeText = (value) => {
  let text;
  if (value instanceof Error) text = `${value.name}: ${value.message}\n${value.stack || ""}`;
  else if (typeof value === "string") text = value;
  else {
    try { text = JSON.stringify(value, null, 2); }
    catch { text = String(value); }
  }
  return String(text || "Unknown error")
    .replace(SECRET_PATTERN, "$1$2[REDACTED]")
    .slice(0, 12000);
};

// An HTTP status standing on its own. A stack frame such as
// "bundle.js:403590:3" contains "403" too: that is a line number, and it made
// a plain React warning look like an authorization failure.
const hasHttpStatus = (text, ...codes) => new RegExp(`(^|[^\\d:.])(${codes.join("|")})(?!\\d)`).test(text);

export const explainError = (message, source) => {
  const value = String(message || "").toLowerCase();
  if (value.includes("failed to fetch") || value.includes("networkerror")) {
    return {
      why: "La requête réseau n’a pas pu atteindre le serveur ou a été bloquée.",
      how: "Vérifier l’URL API, la connexion, CORS et la réponse du serveur dans Network."
    };
  }
  if (value.includes("cannot read") || value.includes("undefined") || value.includes("null")) {
    return {
      why: "Le code a essayé d’utiliser une donnée absente, undefined ou null.",
      how: "Contrôler le payload reçu et protéger l’accès au champ indiqué dans la stack."
    };
  }
  if (hasHttpStatus(value, 401, 403) || value.includes("unauthorized") || value.includes("forbidden")) {
    return {
      why: "L’utilisateur ou le token ne possède pas l’autorisation demandée.",
      how: "Vérifier la session, le rôle, le token et les règles d’autorisation de la route."
    };
  }
  if (hasHttpStatus(value, 404) || value.includes("not found")) {
    return {
      why: "La ressource ou la route demandée n’a pas été trouvée.",
      how: "Vérifier l’identifiant envoyé, l’URL et l’existence de la ressource côté serveur."
    };
  }
  return {
    why: `Une erreur ${source === "console.error" ? "a été signalée par le code" : "JavaScript non gérée s’est produite"}.`,
    how: "Commencer par la première ligne de la stack, reproduire l’action, puis vérifier les données reçues à cet endroit."
  };
};

const makeEntry = ({ message, stack = "", source, file = "", line = 0, column = 0, page }) => {
  const cleanMessage = safeText(message);
  const cleanStack = safeText(stack || "");
  const explanation = explainError(cleanMessage, source);
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    timestamp: new Date().toISOString(),
    page,
    source,
    message: cleanMessage,
    stack: cleanStack,
    where: file ? `${file}${line ? `:${line}${column ? `:${column}` : ""}` : ""}` : (cleanStack.split("\n")[1]?.trim() || page),
    ...explanation,
  };
};

const FamilyErrorDiagnostics = ({ infoSource }) => {
  const location = useLocation();
  const isFamily = getCurrentElakayRole(infoSource) === "Family";
  const [errors, setErrors] = useState([]);
  const [open, setOpen] = useState(false);
  const pageKey = `${location.pathname}${location.search}`;
  const pageErrors = useMemo(() => errors.filter((error) => error.page === pageKey), [errors, pageKey]);

  useEffect(() => {
    if (!isFamily || typeof window === "undefined") return undefined;

    const addError = (entry) => setErrors((current) => {
      const fingerprint = `${entry.page}|${entry.source}|${entry.message}|${entry.where}`;
      if (current.some((item) => item.fingerprint === fingerprint)) return current;
      return [...current, { ...entry, fingerprint }].slice(-MAX_ERRORS);
    });
    const currentPage = () => `${window.location.pathname}${window.location.search}`;
    const onError = (event) => addError(makeEntry({
      message: event.message || event.error,
      stack: event.error?.stack,
      source: "window.error",
      file: event.filename,
      line: event.lineno,
      column: event.colno,
      page: currentPage(),
    }));
    const onRejection = (event) => addError(makeEntry({
      message: event.reason,
      stack: event.reason?.stack,
      source: "unhandledrejection",
      page: currentPage(),
    }));
    const originalConsoleError = console.error;
    console.error = (...args) => {
      originalConsoleError(...args);
      // Deferred: console.error can fire synchronously mid-render (e.g. React's
      // own dev warnings), and calling setState here directly would trigger
      // "Cannot update a component while rendering a different component".
      setTimeout(() => {
        addError(makeEntry({
          message: args.map(safeText).join(" "),
          stack: args.find((arg) => arg instanceof Error)?.stack || new Error().stack,
          source: "console.error",
          page: currentPage(),
        }));
      }, 0);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      console.error = originalConsoleError;
    };
  }, [isFamily]);

  if (!isFamily) return null;

  const copyReport = async () => {
    const report = pageErrors.map(({ timestamp, page, source, message, why, where, how, stack }) =>
      JSON.stringify({ timestamp, page, source, message, why, where, how, stack }, null, 2)
    ).join("\n\n");
    await navigator.clipboard?.writeText(report);
  };

  return (
    <>
      <button
        type="button"
        className={`family-error-button ${pageErrors.length ? "has-errors" : ""}`}
        onClick={() => setOpen(true)}
        title={elakayAutoText("h_94d35d921dedd6", "Erreurs de la page (Développeur seulement)")}
        aria-label={elakayAutoText("h_1198103f8edf88", "Afficher les erreurs de cette page ({value0})", { value0: pageErrors.length })}
      >
        <MdBugReport />
        <span>{pageErrors.length}</span>
      </button>

      <Modal
        show={open}
        onHide={() => setOpen(false)}
        centered
        dialogClassName="family-error-modal-dialog"
        contentClassName="family-error-modal"
        backdropClassName="family-error-backdrop"
        aria-label={elakayAutoText("h_b1cdd4114fc2b1", "Diagnostic des erreurs")}
      >
        <Modal.Header as="header">
          <div><MdBugReport /><div><h2>{elakayAutoText("h_f2d55d4cc795ab", "Diagnostic de la page")}</h2><p>{pageKey}</p></div></div>
          <button type="button" onClick={() => setOpen(false)} aria-label={elakayAutoText("h_5ab4ec640d654d", "Fermer")}><MdClose /></button>
        </Modal.Header>
        <Modal.Body className="family-error-modal-body">
          <div className="family-error-actions">
            <span>{pageErrors.length}{elakayAutoText("h_fe05e93a764e1e", "erreur(s) sur cette page")}</span>
            <button type="button" onClick={copyReport} disabled={!pageErrors.length}><MdContentCopy />{elakayAutoText("h_cdd28e1a695416", "Copier")}</button>
            <button type="button" onClick={() => setErrors((all) => all.filter((item) => item.page !== pageKey))} disabled={!pageErrors.length}><MdDeleteSweep />{elakayAutoText("h_fe23de7b572d52", "Effacer")}</button>
          </div>
          <div className="family-error-list">
            {!pageErrors.length && <div className="family-error-empty">{elakayAutoText("h_cc587c94a0857d", "Aucune erreur capturée sur cette page.")}</div>}
            {[...pageErrors].reverse().map((error, index) => (
              <article key={error.id}>
                <div className="family-error-number">#{pageErrors.length - index} · {new Date(error.timestamp).toLocaleString()}</div>
                <h3>{error.message}</h3>
                <dl>
                  <dt>{elakayAutoText("h_40c6e1b3ee86e5", "Pourquoi ?")}</dt><dd>{error.why}</dd>
                  <dt>{elakayAutoText("h_fdd79a082dd024", "Où ?")}</dt><dd><code>{error.where}</code></dd>
                  <dt>{elakayAutoText("h_60ae2edf01301d", "Comment ?")}</dt><dd>{error.how}</dd>
                  <dt>{elakayAutoText("h_6da13addb000b6", "Source")}</dt><dd>{error.source}</dd>
                </dl>
                {error.stack && <details><summary>{elakayAutoText("h_c65c57df589e7f", "Stack technique")}</summary><pre>{error.stack}</pre></details>}
              </article>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default FamilyErrorDiagnostics;
